import React from "react";
import { Box, Divider, Grid, Link as MuiLink, Typography } from "@mui/material";

import { CartItem } from "@/app/models/cart/types";
import defaultProductImg from "../../../img/default-image_420.png";
import { statusColor, statusLabel } from "@/app/utils/inventoryStatus";
import CartSpecificationLinesView from "../cart/cartSpecificationLinesView";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import { isSAPSource } from "@/app/utils/erpHelper";

export type ItemsInOrderProps = {
  item: CartItem;
};

const formatMoney = (n?: number) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD" })
    : "-";

const ItemsInOrder: React.FC<ItemsInOrderProps> = ({ item }) => {
  const invLabel = statusLabel(item.inventoryStatus);
  const productHref = `/lbpcustomer/product?id=${
    item.parentProductId
      ? `${item.parentProductId}&associatedproduct=${item.productId}`
      : item.productId
  }`;

  let warehouseSource = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.source
  );

  let isSapWarehouse = isSAPSource(warehouseSource ?? "");

  return (
    <Box
      sx={{
        bgcolor: "white",
        p: 1.5,
        mb: "26px",
        borderRadius: 2,
      }}
    >
      <Grid container columnSpacing={2}>
        <Grid
          item
          xs={12}
          sm={2}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <a
            href={productHref}
            title={item.picture?.title ?? item.productName}
            style={{ width: "100%" }}
          >
            <Box
              component="img"
              alt={item.picture?.alternateText ?? item.productName}
              src={
                item.picture?.imageUrl
                  ? `${process.env.REACT_APP_CDN_URL}/images/general/picture_${item.picture.imageUrl}`
                  : defaultProductImg
              }
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                backgroundColor: item.colorHex ?? "transparent",
              }}
            />
          </a>
        </Grid>

        <Grid item xs={12} sm={10}>
          <Grid container>
            <Grid item xs={12} sm={10}>
              <MuiLink
                href={productHref}
                underline="none"
                color="#767676"
                title={item.productName}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 18,
                }}
              >
                <strong>
                  {(item.manufacturers?.[0] ?? "").trim()
                    ? `${item.manufacturers?.[0]} - `
                    : ""}
                </strong>
                {item.productName}
              </MuiLink>
              {!!invLabel && (
                <Typography
                  sx={{
                    mt: 0.5,
                    color: statusColor(item.inventoryStatus),
                    fontSize: 14,
                  }}
                >
                  {invLabel}
                </Typography>
              )}
              {item.availabilityMessage && (
                <Typography sx={{ color: "#000", fontSize: 14 }}>
                  {item.availabilityMessage}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={2}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {formatMoney(item.unitPrice * item.quantity)}
                </Typography>
                {item.discount != null && item.discount !== "" && (
                  <>
                    <Typography
                      sx={{ fontSize: 14, color: "#29b05f", mt: 0.5 }}
                    >
                      You save:
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: "#29b05f" }}>
                      {item.discount}
                    </Typography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: "30px" }}>
            <Grid container>
              <Grid item xs={12} md={7}>
                <Grid container>
                  {!!item.quantity && (
                    <Grid item xs={12}>
                      Quantity:{" "}
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {item.quantity}
                      </Typography>
                    </Grid>
                  )}
                  {!!item.uom && (
                    <Grid item xs={12}>
                      UOM:{" "}
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {item.uom}
                      </Typography>
                    </Grid>
                  )}
                  {!!item.color && (
                    <Grid item xs={12}>
                      Color:{" "}
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {item.color}
                      </Typography>
                    </Grid>
                  )}
                  {!!item.measure && (
                    <Grid item xs={12}>
                      Measure:{" "}
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {item.measure}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    Price:&nbsp;
                    <Typography component="span" sx={{ fontWeight: 700 }}>
                      {item.unitPriceString ??
                        `${formatMoney(item.unitPrice)} per ${
                          item.uom ?? "Unit"
                        }`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{ display: { xs: "block", md: "none" } }}
                  >
                    <Typography variant="body2">
                      SKU:&nbsp;
                      <Typography component="span" sx={{ fontWeight: 700 }}>
                        {isSapWarehouse ? item.sapProduct : item.sku}
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography variant="body2">
                SKU:&nbsp;
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {isSapWarehouse ? item.sapProduct : item.sku}
                </Typography>
              </Typography>
            </Box>
            {/* Spec lines editor */}
            {item.cartSpecificationLine && (
              <Box sx={{ paddingTop: "10px" }}>
                <CartSpecificationLinesView
                  data={item.cartSpecificationLines}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2, borderColor: "rgba(0,0,0,0.08)" }} />
        </Grid>
      </Grid>
    </Box>
  );
};
export default ItemsInOrder;
